import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackAdminAccess from "./useHasStackAdminAccess";

const STACK_MANAGE_ACCESS_TYPES = [Action.StackManage];

const hasStackManageAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(STACK_MANAGE_ACCESS_TYPES, viewer, spaceId);

type StackForManage = Pick<Stack, "spaceDetails" | "space" | "isLockedByTemplateDeployment">;

/**
 * Hook that returns a function to check if the current user can manage a stack, i.e. perform the
 * stack-scoped operations that are not part of the `stackUpdate` mutation: attaching and detaching
 * contexts, policies and cloud integrations, and managing stack webhooks.
 *
 * Use `useCanUpdateStack` for anything backed by `stackUpdate`.
 *
 * Stacks created from a template deployment cannot be managed.
 * @returns {(stack?: StackForManage) => AccessResult} Function that takes a stack and returns AccessResult
 */
const useCanManageStack = (): ((stack?: StackForManage) => AccessResult) => {
  const hasAdminAccess = useHasStackAdminAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack) => {
      if (!stack) {
        return { result: false };
      }

      const templateDeploymentCheck = checkTemplateDeploymentAccess(stack);
      if (templateDeploymentCheck) {
        return templateDeploymentCheck;
      }

      const hasAccess =
        hasAdminAccess(stack).result ||
        hasStackManageAccess(viewer, stack.spaceDetails?.id || stack.space);

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasAdminAccess, viewer]
  );
};

export default useCanManageStack;
